'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  useLocale,
  useBlockPart,
  useMarketplaceSource,
  BlockIcon,
} from '@akademi/site-builder-runtime';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@akademi/ui';
import { BookOpen, TriangleAlert, X } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { tx } from '../_shared/coerce';
import { textTertiary } from '../_shared/styles';
import { formatMarketPrice, mpSurface, type MarketPrice } from './_shared';

type Localized = string | Record<string, string>;

export interface MarketplacePendingOrder {
  id: string;
  total?: MarketPrice;
  createdAt?: string;
  items?: { title?: Localized }[];
}

export interface MarketplacePendingOrdersProps {
  title?: Localized;
  icon?: string;
  buttonIcon?: string;
  /** `'pending'` (default) lists only orders awaiting payment — the cart-page
   *  widget's job; `'all'` lists every order — the `/marketplace/orders`
   *  history page. Only affects the live-source path. */
  status?: 'pending' | 'all';
  /** Hide the "awaiting payment" warning banner (the order-history page
   *  doesn't want it). */
  hideWarning?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const PAY_HREF_TEMPLATE = '/marketplace/orders/{id}';

const DEFAULT_ORDERS: MarketplacePendingOrder[] = [
  {
    id: '10432',
    total: { amount: '1499.00', currency: 'TRY' },
    createdAt: '2026-08-28',
    items: [{ title: 'Leadership Essentials' }],
  },
];

/**
 * Orders widget — akademi-lms-v2's `ShoppingCartPage` "non-payment orders"
 * section (and, with `status="all"` + `hideWarning`, the
 * `/marketplace/orders` history page). With a {@link useMarketplaceSource}
 * mounted it lists real purchase orders (`POST
 * /marketplace/purchase-order/search`) and "cancel" hits the live endpoint;
 * with no source it's authored/demo rows removed locally — the "fallbacks
 * stay, for testing" contract.
 */
export function MarketplacePendingOrders({
  title,
  icon = 'clock',
  buttonIcon = 'credit-card',
  status = 'pending',
  hideWarning = false,
  paddingOuter,
  paddingInner,
}: MarketplacePendingOrdersProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const part = useBlockPart();
  const source = useMarketplaceSource();
  const [localRows, setLocalRows] = useState<MarketplacePendingOrder[]>(DEFAULT_ORDERS);
  const [fetched, setFetched] = useState<MarketplacePendingOrder[] | null>(null);

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    source
      .listOrders(status === 'pending' ? { status: 'PENDING' } : {})
      .then((listing) => {
        if (cancelled) return;
        setFetched(
          listing.items.map((o) => ({
            id: o.id,
            total: o.total,
            createdAt: o.createdAt,
            items: o.items?.map((it) => ({ title: it.title })),
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setFetched([]);
      });
    return () => {
      cancelled = true;
    };
  }, [source, status]);

  const rows = source ? (fetched ?? []) : localRows;

  const cancel = (id: string) => {
    if (source) {
      void source
        .cancelOrder(id)
        .then(() => setFetched((prev) => prev?.filter((o) => o.id !== id) ?? null));
    } else {
      setLocalRows((prev) => prev.filter((o) => o.id !== id));
    }
  };

  if (rows.length === 0) return null;

  return (
    <SectionShell paddingOuter={paddingOuter}>
      <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
        <CardHeader className="p-0">
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2, 8px)' }}>
            <BlockIcon value={icon} size={18} />
            {tx(title) || t('marketplace.pendingOrders.title')}
          </CardTitle>
        </CardHeader>
        <CardContent
          className="px-0 pb-0 pt-3"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)' }}
        >
          {!hideWarning && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2, 8px)',
                color: 'var(--color-amber-700, #b45309)',
                background: 'color-mix(in srgb, var(--color-amber-500, #f59e0b) 12%, transparent)',
                borderRadius: 'var(--radius-md, 8px)',
                padding: 'var(--space-3, 12px)',
                fontSize: 'var(--font-size-sm, 13px)',
              }}
            >
              <TriangleAlert size={16} />
              {t('marketplace.pendingOrders.warning')}
            </div>
          )}

          <Accordion type="multiple" {...part('list')}>
            {rows.map((order, i) => {
              const parsedDate = order.createdAt ? new Date(order.createdAt) : null;
              const dateText =
                parsedDate && !Number.isNaN(parsedDate.getTime())
                  ? parsedDate.toLocaleDateString(locale)
                  : undefined;
              const hasItems = Boolean(order.items && order.items.length > 0);
              return (
                <AccordionItem key={order.id} value={order.id} {...part('item', i)}>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--space-2, 8px)',
                      padding: 'var(--space-3, 12px) 0',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Badge variant="outline">
                        {t('marketplace.pendingOrders.order', { id: order.id })}
                      </Badge>
                      {dateText && (
                        <span style={{ ...textTertiary, fontSize: 'var(--font-size-xs, 12px)' }}>
                          {dateText}
                        </span>
                      )}
                    </div>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3, 12px)' }}
                    >
                      <strong>{formatMarketPrice(order.total, locale)}</strong>
                      <Button asChild size="sm" variant="brand">
                        <PortalLink
                          href={PAY_HREF_TEMPLATE.replace('{id}', order.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <BlockIcon value={buttonIcon} size={14} />
                          {t('marketplace.pendingOrders.makePayment')}
                        </PortalLink>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => cancel(order.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <X size={14} aria-hidden />
                        {t('marketplace.pendingOrders.cancel')}
                      </Button>
                    </div>
                  </div>
                  {hasItems && (
                    <>
                      <AccordionTrigger>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <BookOpen size={14} aria-hidden />
                          {t('marketplace.pendingOrders.itemCount', { count: order.items!.length })}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          {order.items!.map((it, itemIndex) => (
                            <div
                              key={itemIndex}
                              style={{
                                fontSize: 'var(--font-size-sm, 13px)',
                                padding: 'var(--space-2, 8px) 0',
                                borderTop:
                                  itemIndex > 0
                                    ? '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))'
                                    : undefined,
                              }}
                            >
                              {tx(it.title)}
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </>
                  )}
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </SectionShell>
  );
}
